import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ChevronRight, UserRound, Users } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useReserveContext } from '../../../contexts/useReserveContext';
import { fetchServicesForDateSelection } from '../../../utils/reservation/by-Date/fetchServicesForDateSelection';
import { fetchStaffOptionsForDate } from '../../../utils/reservation/by-Date/fetchStaffOptionsForDate';
import { type StaffForDate } from '../../../utils/reservation/by-Date/fetchStaffsForDate';
import { fetchStaffServiceDurations } from '../../../utils/reservation/by-service/fetchStaffServiceDurations';

import ReserveStepHeader from '../ReserveStepHeader';
import StaffSelectionCard from '../StaffSelectionCard';

const ANY_STAFF_LABEL = 'Anyone is okay';

const SelectStaffByDate: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEditing = searchParams.get('edit') === '1';
  const {
    storeId,
    selectedDate,
    selectedStartTime,
    selectedStaffId,
    isFreeReservation,
    setSelectedStaffId,
    setSelectedStaffName,
    setSelectedServices,
    setIsFreeReservation,
    selectedServices,
  } = useReserveContext();

  const [staffs, setStaffs] = useState<StaffForDate[]>([]);
  const [loading, setLoading] = useState(false);
  const [freeAvailable, setFreeAvailable] = useState(false);
  const serviceIds = useMemo(
    () =>
      selectedServices
        .map((service) => Number(service.serviceId))
        .filter((serviceId) => Number.isFinite(serviceId) && serviceId > 0),
    [selectedServices],
  );

  useEffect(() => {
    if (!storeId) {
      toast.info('Please select a store first');
      navigate('/reserve/by-date/store');
      return;
    }
    if (!selectedDate) {
      toast.info('Please select a date first');
      navigate('/reserve/by-date/date');
      return;
    }
    if (!selectedStartTime) {
      toast.info('Please select a time first');
      navigate('/reserve/by-date/time');
      return;
    }
    if (isEditing && serviceIds.length === 0) {
      toast.info('Please select services first');
      navigate(isEditing ? '/reserve/by-date/confirm-reservation' : '/reserve/by-date/service');
      return;
    }
  }, [storeId, selectedDate, selectedStartTime, serviceIds, isEditing, navigate]);

  useEffect(() => {
    const load = async () => {
      if (!storeId || !selectedDate || !selectedStartTime) return;
      setLoading(true);
      try {
        if (!isEditing && serviceIds.length === 0) {
          const services = await fetchServicesForDateSelection({
            storeId,
            date: selectedDate,
            anyone: true,
          });
          const serviceIdsForTime = services
            .map((service) => Number(service.service_id))
            .filter((serviceId) => Number.isFinite(serviceId) && serviceId > 0);
          const staffMap = new Map<number, StaffForDate>();
          let nextFreeAvailable = false;

          await Promise.all(
            serviceIdsForTime.map(async (serviceId) => {
              const options = await fetchStaffOptionsForDate({
                storeId,
                date: selectedDate,
                startTime: selectedStartTime!,
                serviceIds: [serviceId],
              });
              nextFreeAvailable = nextFreeAvailable || options.free_available;
              options.staffs.forEach((staff) => {
                if (!staffMap.has(staff.staff_id)) {
                  staffMap.set(staff.staff_id, {
                    id: staff.staff_id,
                    name: staff.name,
                    nickname: staff.nickname,
                    icon_url: staff.icon_url,
                    bio: staff.bio,
                    role_name: staff.role_name,
                    sns_links: staff.sns_links,
                  });
                }
              });
            }),
          );

          setFreeAvailable(nextFreeAvailable);
          setStaffs(Array.from(staffMap.values()));
          return;
        }

        const options = await fetchStaffOptionsForDate({
          storeId,
          date: selectedDate,
          startTime: selectedStartTime!,
          serviceIds,
        });
        setFreeAvailable(options.free_available);
        setStaffs(
          options.staffs.map((staff) => ({
            id: staff.staff_id,
            name: staff.name,
            nickname: staff.nickname,
            icon_url: staff.icon_url,
            bio: staff.bio,
            role_name: staff.role_name,
            sns_links: staff.sns_links,
          })),
        );
      } catch (err: any) {
        console.error(err);
        toast.error(err?.message || 'Failed to load staff list');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [storeId, selectedDate, selectedStartTime, serviceIds, isEditing]);

  const handleSelectSpecificStaff = async (staff: StaffForDate) => {
    const displayName = staff.nickname || staff.name || '';
    if (!isEditing && serviceIds.length === 0) {
      setSelectedStaffId(staff.id);
      setSelectedStaffName(displayName);
      setSelectedServices([]);
      setIsFreeReservation(false);
      navigate('/reserve/by-date/service');
      return;
    }

    try {
      const durations = await fetchStaffServiceDurations(staff.id, serviceIds);
      const durationMap = new Map(
        durations.map((row) => [Number(row.service_id), Number(row.duration_minutes)]),
      );

      setSelectedStaffId(staff.id);
      setSelectedStaffName(displayName);
      setSelectedServices(
        selectedServices.map((service) => ({
          ...service,
          duration_minutes:
            durationMap.get(Number(service.serviceId)) ?? Number(service.duration_minutes) ?? 0,
          staffId: staff.id,
          staffName: displayName,
        })),
      );
      setIsFreeReservation(false);
      navigate('/reserve/by-date/confirm-reservation');
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || 'Failed to update the selected staff');
    }
  };

  const handleSelectAnyone = () => {
    if (!freeAvailable) return;
    setSelectedServices(
      selectedServices.map((service) => ({
        ...service,
        staffId: null,
        staffName: ANY_STAFF_LABEL,
      })),
    );
    setSelectedStaffId(null);
    setSelectedStaffName(ANY_STAFF_LABEL);
    setIsFreeReservation(true);
    navigate(serviceIds.length === 0 ? '/reserve/by-date/service' : '/reserve/by-date/confirm-reservation');
  };

  const hasOptions = staffs.length > 0;

  return (
    <div className="flex flex-col min-h-[70vh] space-y-4">
      <ReserveStepHeader
        title="Choose your staff"
        onBack={() =>
          navigate(isEditing ? '/reserve/by-date/confirm-reservation' : '/reserve/by-date/time')
        }
        step={4}
        totalSteps={5}
        description={
          isEditing
            ? 'Choose another staff option while keeping the current date, time, and services.'
            : 'Choose who you want before selecting services.'
        }
      />

      {loading ? (
        <p className="text-center text-sm text-slate-400">Loading staff list...</p>
      ) : (
        <div className="space-y-3">
          {freeAvailable ? (
            <button
              type="button"
              onClick={handleSelectAnyone}
              className={[
                'w-full rounded-[28px] border px-4 py-4 text-left shadow-sm transition',
                isFreeReservation
                  ? 'border-[#0ABAB5] bg-[linear-gradient(135deg,#0ABAB5,#089d99)] text-white shadow-[0_14px_28px_rgba(10,186,181,0.22)]'
                  : 'border-[#ccefed] bg-[linear-gradient(180deg,rgba(244,253,252,0.98),rgba(255,255,255,0.96))] text-slate-800 hover:border-[#96dfdb] hover:bg-[#f3fbfa]',
              ].join(' ')}
            >
              <div className="flex items-center gap-4">
                <div className={`rounded-3xl p-3 ${isFreeReservation ? 'bg-white/15 text-white' : 'bg-[#dff8f6] text-[#087f7c]'}`}>
                  <Users className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-base font-semibold">{ANY_STAFF_LABEL}</div>
                  <p className={`mt-1 text-xs ${isFreeReservation ? 'text-white/90' : 'text-slate-500'}`}>
                    Book this as a Free reservation and decide the actual staff later.
                  </p>
                </div>
                <div className="ml-auto flex items-center gap-2">
                  {isFreeReservation ? (
                    <UserRound className="h-5 w-5 text-white" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-slate-300" />
                  )}
                </div>
              </div>
            </button>
          ) : null}

          {!hasOptions && !freeAvailable ? (
            isEditing ? (
              <div className="rounded-[28px] border border-rose-200 bg-rose-50 p-4 shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="rounded-full bg-rose-100 p-2 text-rose-600">
                    <AlertTriangle className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-rose-700">Staff cannot be changed</div>
                    <div className="mt-1 text-sm leading-6 text-rose-600">
                      The current date, time, and services combination leaves no alternative staff options.
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-center text-sm text-slate-400">
                There are no staff options available for this date, time, and services.
              </p>
            )
          ) : staffs.map((staff) => {
            const displayName = staff.nickname || staff.name || '-';
            const isSelected = !isFreeReservation && selectedStaffId === staff.id;

            return (
              <StaffSelectionCard
                key={staff.id}
                onSelect={() => handleSelectSpecificStaff(staff)}
                name={displayName}
                subLabel={
                  staff.nickname && staff.name && staff.nickname !== staff.name
                    ? `(${staff.name})`
                    : null
                }
                roleName={staff.role_name}
                bio={staff.bio}
                iconUrl={staff.icon_url}
                snsLinks={staff.sns_links}
                isSelected={isSelected}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SelectStaffByDate;
